/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

/**
 * Theme Cookie Utilities
 *
 * Manages theme preference storage in cookies (for SSR) and localStorage (for fallback).
 * Cookies are accessible during server-side rendering, preventing theme flash.
 *
 * Industry best practice: Use cookies for Next.js theme persistence because:
 * - Cookies are accessible during SSR (server can read them)
 * - localStorage is client-only (server can't read it)
 * - Server can render with correct theme from the start
 */

const THEME_COOKIE_NAME = "theme-preference";
const THEME_RESOLVED_COOKIE_NAME = "theme-resolved";
const ORG_THEME_COOKIE_NAME = "org-theme-colors";
const DENSITY_COOKIE_NAME = "ui-density";
const THEME_COOKIE_MAX_AGE = 31536000; // 1 year in seconds
const THEME_LOCALSTORAGE_KEY = "user-theme-preference";
const DENSITY_LOCALSTORAGE_KEY = "ui-density";

/** Root font-size values for each density level */
export const DENSITY_FONT_SIZE: Record<string, string> = {
  compact: "12px",
  comfortable: "14px",
  spacious: "16px",
};

/**
 * Get theme preference from cookie
 * @returns Theme preference string ("light", "dark", "system") or null
 */
export function getThemeCookie(): string | null {
  if (typeof document === "undefined") {
    return null; // Server-side: use server cookie reading
  }

  try {
    const cookies = document.cookie.split("; ");
    const themeCookie = cookies.find((cookie) =>
      cookie.startsWith(`${THEME_COOKIE_NAME}=`),
    );
    if (themeCookie) {
      return themeCookie.split("=")[1] ?? null;
    }
    return null;
  } catch (error) {
    // Silently fail if cookies are not available
    return null;
  }
}

/**
 * Set theme preference in cookie
 * @param theme - Theme preference ("light", "dark", "system")
 */
export function setThemeCookie(theme: string): void {
  if (typeof document === "undefined") {
    return; // Server-side: use server cookie setting
  }

  try {
    // Set cookie with appropriate attributes
    // path=/ ensures cookie is available site-wide
    // max-age sets expiration (1 year)
    // SameSite=Lax allows cookie to be sent with same-site requests
    document.cookie = `${THEME_COOKIE_NAME}=${theme}; path=/; max-age=${THEME_COOKIE_MAX_AGE}; SameSite=Lax`;
  } catch (error) {
    // Silently fail if cookies are not available (e.g., incognito mode with restrictions)
  }
}

/**
 * Get resolved theme from cookie
 * @returns "light" | "dark" or null
 */
export function getResolvedThemeCookie(): string | null {
  if (typeof document === "undefined") {
    return null;
  }

  try {
    const cookies = document.cookie.split("; ");
    const resolvedCookie = cookies.find((cookie) =>
      cookie.startsWith(`${THEME_RESOLVED_COOKIE_NAME}=`),
    );
    if (resolvedCookie) {
      return resolvedCookie.split("=")[1] ?? null;
    }
    return null;
  } catch (error) {
    return null;
  }
}

/**
 * Set resolved theme in cookie
 * @param resolvedTheme - "light" | "dark"
 */
export function setResolvedThemeCookie(resolvedTheme: string): void {
  if (typeof document === "undefined") {
    return;
  }

  try {
    document.cookie = `${THEME_RESOLVED_COOKIE_NAME}=${resolvedTheme}; path=/; max-age=${THEME_COOKIE_MAX_AGE}; SameSite=Lax`;
  } catch (error) {
    // Silently fail if cookies are not available
  }
}

/**
 * Set organization theme colors in cookie
 * @param primaryColor - Hex primary color
 * @param secondaryColor - Hex secondary color
 */
export function setOrgThemeCookie(
  primaryColor: string,
  secondaryColor: string,
): void {
  if (typeof document === "undefined") {
    return;
  }

  try {
    const payload = encodeURIComponent(
      JSON.stringify({ primaryColor, secondaryColor }),
    );
    document.cookie = `${ORG_THEME_COOKIE_NAME}=${payload}; path=/; max-age=${THEME_COOKIE_MAX_AGE}; SameSite=Lax`;
  } catch (error) {
    // Silently fail if cookies are not available
  }
}

/**
 * Get theme preference from cookie or localStorage (fallback)
 * Checks cookie first (for SSR compatibility), then localStorage
 * CRITICAL: Automatically migrates localStorage to cookie if cookie doesn't exist
 * This ensures cookie is set for future page loads (including new tabs)
 * @returns Theme preference string ("light", "dark", "system") or null
 */
export function getThemePreference(): string | null {
  // Check cookie first (server-accessible)
  const cookieTheme = getThemeCookie();
  if (cookieTheme) {
    return cookieTheme;
  }

  // Fallback to localStorage (client-only, backward compatibility)
  // CRITICAL: Migrate localStorage to cookie if cookie doesn't exist
  // This ensures cookie is set for future page loads (including new tabs)
  if (typeof window !== "undefined" && window.localStorage) {
    try {
      const localStorageTheme = localStorage.getItem(THEME_LOCALSTORAGE_KEY);
      if (localStorageTheme) {
        // Migrate to cookie immediately so it's available for future loads
        // This is a one-time migration for existing users
        setThemeCookie(localStorageTheme);
        return localStorageTheme;
      }
    } catch (error) {
      // Silently fail if localStorage is not available
      return null;
    }
  }

  return null;
}

/**
 * Set theme preference in both cookie and localStorage
 * Cookie is primary (for SSR), localStorage is fallback (for backward compatibility)
 * @param theme - Theme preference ("light", "dark", "system")
 */
export function setThemePreference(theme: string): void {
  // Set cookie (primary - server-accessible)
  setThemeCookie(theme);

  // Also set localStorage (fallback - backward compatibility)
  if (typeof window !== "undefined" && window.localStorage) {
    try {
      localStorage.setItem(THEME_LOCALSTORAGE_KEY, theme);
    } catch (error) {
      // Silently fail if localStorage is not available
    }
  }
}

/**
 * Clear organization theme colors from cookie
 */
export function clearOrgThemeCookie(): void {
  if (typeof document === "undefined") {
    return;
  }

  try {
    document.cookie = `${ORG_THEME_COOKIE_NAME}=; path=/; max-age=0; SameSite=Lax`;
  } catch (error) {
    // Silently fail if cookies are not available
  }
}

/**
 * Get UI density preference from cookie or localStorage
 */
export function getDensityPreference(): string | null {
  if (typeof document === "undefined") return null;

  try {
    const cookies = document.cookie.split("; ");
    const cookie = cookies.find((c) => c.startsWith(`${DENSITY_COOKIE_NAME}=`));
    if (cookie) return cookie.split("=")[1] ?? null;
  } catch {
    // fall through
  }

  if (typeof window !== "undefined") {
    try {
      const ls = localStorage.getItem(DENSITY_LOCALSTORAGE_KEY);
      if (ls) {
        setDensityPreference(ls); // migrate to cookie
        return ls;
      }
    } catch {
      // fall through
    }
  }

  return null;
}

/**
 * Set UI density preference in cookie and localStorage
 */
export function setDensityPreference(density: string): void {
  if (typeof document !== "undefined") {
    try {
      document.cookie = `${DENSITY_COOKIE_NAME}=${density}; path=/; max-age=${THEME_COOKIE_MAX_AGE}; SameSite=Lax`;
    } catch {
      // ignore
    }
  }
  if (typeof window !== "undefined") {
    try {
      localStorage.setItem(DENSITY_LOCALSTORAGE_KEY, density);
    } catch {
      // ignore
    }
  }
}

/**
 * Apply density to the DOM by setting root font-size
 */
export function applyDensityToDOM(density: string): void {
  if (typeof document === "undefined") return;
  const size = DENSITY_FONT_SIZE[density] ?? "14px";
  document.documentElement.style.fontSize = size;
}

/**
 * Clear theme preference from both cookie and localStorage
 */
export function clearThemePreference(): void {
  // Clear cookie
  if (typeof document !== "undefined") {
    try {
      document.cookie = `${THEME_COOKIE_NAME}=; path=/; max-age=0; SameSite=Lax`;
      document.cookie = `${THEME_RESOLVED_COOKIE_NAME}=; path=/; max-age=0; SameSite=Lax`;
    } catch (error) {
      // Silently fail
    }
  }

  // Clear localStorage
  if (typeof window !== "undefined" && window.localStorage) {
    try {
      localStorage.removeItem(THEME_LOCALSTORAGE_KEY);
    } catch (error) {
      // Silently fail
    }
  }
}
